import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { ArrowLeft, MessageSquare, Trash2, Save } from "lucide-react";

import { updateContact } from "slices/thunk";
import avatar from "assets/images/users/user-2.jpg";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { Separator } from "components/ui/separator";
import { cn } from "lib/utils";

interface ContactDetailProps {
  contactDetail: any;
  loadingDetail: boolean;
  onDelete: () => void;
  onChatOpen: () => void;
  onBackToContactList?: () => void;
}

const initials = (name?: string) =>
  (name ?? "")
    .split(" ")
    .map((w: string) => w.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "??";

const FIELDS: {
  field: string;
  label: string;
  required?: boolean;
  type?: string;
}[] = [
  { field: "displayName", label: "Display Name", required: true },
  { field: "firstName", label: "First Name" },
  { field: "lastName", label: "Last Name" },
  { field: "email", label: "Email", type: "email" },
  { field: "phone", label: "Phone" },
  { field: "company", label: "Company" },
  { field: "address", label: "Address" },
  { field: "description", label: "Description" },
];

const renderSummary = (summary?: string) => {
  if (!summary) return null;
  if (!summary.includes("**")) return summary;
  const parts = summary.split("**").filter((s) => s.trim());
  const sections: { header: string; content: string }[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const header = parts[i]?.trim() || "";
    const content = parts[i + 1]?.trim().replace(/^\*\* /, "") || "";
    sections.push({ header, content });
  }
  return sections.map((section, index) => (
    <p key={index} className="mb-2 last:mb-0">
      <strong className="text-foreground">{section.header}</strong>{" "}
      {section.content}
    </p>
  ));
};

const ContactDetail: React.FC<ContactDetailProps> = ({
  contactDetail,
  loadingDetail,
  onDelete,
  onChatOpen,
  onBackToContactList,
}) => {
  const dispatch = useDispatch<any>();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: contactDetail?.name || "",
      firstName: contactDetail?.firstName || "",
      lastName: contactDetail?.lastName || "",
      email: contactDetail?.email || "",
      phone: contactDetail?.phoneNumber || "",
      description: contactDetail?.description || "",
      company: contactDetail?.company || "",
      address: contactDetail?.address || "",
    },
    validationSchema: Yup.object({
      displayName: Yup.string().required("Display name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .test(
          "email-required-if-no-phone",
          "Either email or phone is required",
          function (value) {
            const { phone } = this.parent;
            return !!value || !!phone;
          }
        ),
      phone: Yup.string()
        .matches(
          /^\+1\d{10}$/,
          "Phone number must start with +1 (USA code) followed by 10 digits"
        )
        .test(
          "phone-required-if-no-email",
          "Either email or phone is required",
          function (value) {
            const { email } = this.parent;
            return !!value || !!email;
          }
        ),
    }),
    onSubmit: (values: any) => {
      const payload: any = {
        name: values.displayName,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        PhoneNumber: values.phone,
        description: values.description,
        company: values.company,
        address: values.address,
      };
      if (contactDetail?.id) {
        dispatch(updateContact({ id: contactDetail.id, payload }));
      }
    },
  });

  if (loadingDetail) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-card">
      {/* Mobile back button */}
      {onBackToContactList ? (
        <div className="flex items-center border-b border-border/60 p-3 xl:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBackToContactList}
            aria-label="Back to contact list"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
      ) : null}

      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:px-8 lg:py-10">
        {/* Form column */}
        <div className="flex-1">
          {/* Profile header */}
          <div className="mb-8 flex flex-col items-center text-center">
            <Avatar className="h-20 w-20">
              {contactDetail?.image ? (
                <AvatarImage src={avatar} alt={contactDetail?.name} />
              ) : null}
              <AvatarFallback className="text-lg">
                {initials(contactDetail?.name)}
              </AvatarFallback>
            </Avatar>
            <h2 className="mt-3 text-xl font-semibold tracking-tight text-foreground">
              {contactDetail?.name || "Unnamed contact"}
            </h2>
            {contactDetail?.email ? (
              <p className="text-sm text-muted-foreground">
                {contactDetail.email}
              </p>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={onChatOpen}
            >
              <MessageSquare className="h-4 w-4" />
              Open conversation
            </Button>
          </div>

          <Separator className="mb-6" />

          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FIELDS.filter((f) => f.field !== "description").map(
                ({ field, label, required, type }) => {
                  const fieldKey = field as keyof typeof formik.values;
                  const error = formik.touched[fieldKey] &&
                    typeof formik.errors[fieldKey] === "string"
                    ? (formik.errors[fieldKey] as string)
                    : null;
                  return (
                    <div key={field} className="space-y-1.5">
                      <Label htmlFor={`contact-${field}`}>
                        {label}
                        {required && (
                          <span className="ml-1 text-destructive">*</span>
                        )}
                      </Label>
                      <Input
                        id={`contact-${field}`}
                        name={field}
                        type={type ?? "text"}
                        placeholder={`Enter ${label.toLowerCase()}`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[fieldKey] || ""}
                        aria-invalid={!!error}
                        className={cn(error && "border-destructive/60")}
                      />
                      {error ? (
                        <p className="text-xs font-medium text-destructive">
                          {error}
                        </p>
                      ) : null}
                    </div>
                  );
                }
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-description">Description</Label>
              <textarea
                id="contact-description"
                name="description"
                placeholder="Enter description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description || ""}
                rows={4}
                className={cn(
                  "flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm transition-all",
                  "placeholder:text-muted-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-primary/40"
                )}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete();
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4" />
                Update
              </Button>
            </div>
          </form>
        </div>

        {/* Conversation summary aside */}
        {contactDetail?.conversationSummary &&
        contactDetail.conversationSummary.includes("**") ? (
          <aside className="hidden w-full max-w-sm shrink-0 lg:block">
            <div className="sticky top-4 rounded-xl border border-border/60 bg-muted/30 p-5 shadow-soft">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Conversation Summary
              </h3>
              <div className="max-h-[600px] overflow-y-auto text-sm leading-relaxed text-muted-foreground">
                {renderSummary(contactDetail.conversationSummary)}
              </div>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
};

export default ContactDetail;
